/**
 * OrderConfirmSheet, Sprint 1.7 inline gift request confirmation.
 *
 * Opened from the chat widget OR the browse picker's "Send →" action.
 * Captures the final delivery details (date, address, optional notes)
 * then calls submit-gift-request to create a celebration_recommendations
 * row + alert the Lembra ops team for manual confirmation.
 *
 * UI:
 *   - Centered modal on desktop (max-w-md)
 *   - Bottom sheet on mobile (slides up, full-width)
 *   - Escape closes; focus trap stays inside while open
 *
 * Pioneer alignment:
 *   - Krug: one screen, three editable fields, one CTA
 *   - Wroblewski: smart defaults (date from celebration, address from
 *     company), single column, mobile-first
 *   - Krishna: only ask for what the chat/browse didn't already capture
 *   - Stanley: alt text on SKU image, aria-modal, focus trap, ESC + ✕
 *   - Nielsen #5: explicit "no charge until you confirm again" copy
 */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, Loader2, ExternalLink, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { captureInteraction } from "@/lib/captureInteraction";
import { SkuCardData } from "@/components/chat/SkuCard";
// Sprint F: Stripe-native promo codes need the buyer's email at
// validate time so the edge fn can enforce the customer-binding the
// Stripe Promotion Code carries. Anon users supply it via the
// "your email" field already on this sheet; authed users get it
// from useAuth (the session.user.email).
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/queries";
import {
  chargeSavedCardForOrder,
  type ChargeSavedCardLineItem,
} from "@/lib/chargeSavedCardForOrder";
import {
  cardNotePlaceholder,
  normalizeOccasionForConfirm,
} from "@/lib/orderConfirmPrefill";
// MS289: wire Google Places autocomplete onto the delivery address
// field. The hook attaches a ref to any input and silently degrades
// to plain text entry if Maps fails to load (network / key issue),
// so we can use it without a fallback branch.
import { useAddressAutocomplete } from "@/hooks/useAddressAutocomplete";
// 2026-05-16 UX-copy audit: dedupe vendor / "Voucher" / "$N" tokens
// from the SKU recap at the top of the order sheet so the same SKU
// reads identically here, on the card, and inside the detail modal.
import { formatSkuName, formatSkuPrice } from "@/lib/formatSkuName";
import type { CheckoutAddon, ProductCheckoutCameFrom } from "@/hooks/useProductCheckoutFlow";
import type { FulfillmentReturnContext } from "@/lib/fulfillmentReturn";
import { FulfillmentReturnLink } from "@/components/lembra/FulfillmentReturnLink";
// MS489: Sentry capture for the interstate-interest failure path so a
// future regression surfaces with structured context instead of the
// opaque "couldn't log your request" copy users have reported.
import { Sentry } from "@/lib/sentry";

interface OrderConfirmSheetProps {
  /** The SKU the customer picked. Rendered in the recap. */
  sku: SkuCardData;
  /** MS594: same-vendor add-ons selected in ProductDetailModal. */
  selectedAddons?: CheckoutAddon[];
  /** Recipient name (display only, the ops team verifies via recipient_id). */
  recipientName: string;
  /** Optional UUID for downstream linking. */
  recipientId?: string | null;
  /** birthday | work_anniversary | welcome | farewell | milestone | other. */
  occasion: string;
  /** Optional default delivery date YYYY-MM-DD (e.g. from the linked celebration). */
  defaultDate?: string;
  /** Optional default delivery address (e.g. company default). */
  defaultAddress?: string;
  /** Where this sheet was opened from, for telemetry. */
  // MS26: "vendor" added so per-vendor landing pages pipe their own
  // attribution through to interaction_captures + the order row.
  // MS59: "landing" added for the inline-concierge surface on the
  // landing hero so we can split landing-inline orders from /chat
  // orders in analytics (was the whole point of inlining).
  // MS64a: "wishlist" + "dashboard" added so authed surfaces (saved-
  // gift cards, dashboard saved-gifts cluster) flow through the same
  // ProductDetailModal → OrderConfirmSheet pipe instead of bouncing
  // to /chat. Lets us split "buyer rediscovered a saved gift" funnel
  // from cold chat / browse acquisition.
  cameFrom: ProductCheckoutCameFrom;
  /** When set, surfaces an explicit return path (e.g. Back to Orders). */
  fulfillmentReturn?: FulfillmentReturnContext | null;
  /** Close handler, called on ✕, ESC, or after success ack. */
  onClose: () => void;
  /** Called after a successful submit so the parent can react (e.g.
   *  the chat page might lock the conversation, or the browse page
   *  might surface a confirmation toast). Optional. */
  onSubmitted?: (requestId: string) => void;
  // Sprint 1.7c anonymous-customer path. When true, the sheet renders
  // extra fields (your name + email) and passes them to the edge fn
  // as guest_name + guest_email so Lembra has a way to send the
  // payment confirmation.
  isAnonymous?: boolean;
  guestSessionId?: string | null;
  // Sprint 1.7p, chat_session_id for the operator transcript viewer.
  // Set when the sheet was opened from the chat surface (Chat.tsx
  // passes it through). Browse opens without it.
  chatSessionId?: string | null;
  // Sprint 2.5a /orders-new-redirect follow-up, celebration_id
  // links the resulting order back to the source celebration row
  // when the chat was entered from a calendar/notification approval.
  // Without this, the celebration's "needs order" state would
  // persist forever even after the customer ordered through chat.
  // Null when the chat wasn't entered from a celebration approval
  // (default cold /chat starts, smart-input briefs, etc.).
  celebrationId?: string | null;
  /** Bulk period checkout — one invoice for celebration_ids[]. */
  bulkCheckout?: {
    bulkRunId: string;
    bulkPeriodKey: string;
    celebrationIds: string[];
  } | null;
}

function todayPlus(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// MS355: interstate-address detection for the order-flow capture CTA.
// Lembra's vendor network is most established in Melbourne today; an
// address outside Melbourne metro can't be fulfilled by the current
// catalogue. Rather than let the customer hit submit and have Lembra
// ops manually decline, we detect at the address-entry step and swap
// the standard submit for a "Request this for [city]" capture form
// that lands in the same `remote_celebration_requests` waitlist the
// chat-concierge uses (MS55) — one signal source for expansion.
//
// The detection is intentionally conservative: ONLY trigger when we
// see a clear interstate marker (NSW/QLD/WA/SA/TAS/ACT/NT state code
// OR a non-Melbourne capital city name). VIC + Melbourne suburb names
// + ambiguous addresses fall through to the normal submit path.
// Lembra ops can still see the address on the resulting order if a
// suburb-named address slips past detection.
const INTERSTATE_STATE_CODES = [" NSW", " QLD", " WA", " SA", " TAS", " ACT", " NT"];
const INTERSTATE_CITY_NAMES = [
  "Sydney", "Brisbane", "Perth", "Adelaide",
  "Hobart", "Darwin", "Canberra",
];

interface InterstateDetection {
  isInterstate: boolean;
  /** Best-guess city/region label for the CTA copy. "Sydney", "Brisbane",
   *  "your area" fallback. */
  cityLabel: string;
}

function detectInterstate(address: string): InterstateDetection {
  if (!address || address.length < 4) {
    return { isInterstate: false, cityLabel: "your area" };
  }
  // City name first — more informative for the CTA label.
  for (const city of INTERSTATE_CITY_NAMES) {
    if (address.includes(city)) {
      return { isInterstate: true, cityLabel: city };
    }
  }
  // State code fallback — caught later in the address string.
  // Uppercase the address for a robust state-code match (Google Places
  // returns uppercase state codes consistently, but manual typing can
  // be mixed-case).
  const upper = " " + address.toUpperCase();
  for (const state of INTERSTATE_STATE_CODES) {
    if (upper.includes(state)) {
      // Map state code → readable label for the CTA.
      const stateMap: Record<string, string> = {
        " NSW": "NSW",
        " QLD": "Queensland",
        " WA": "Western Australia",
        " SA": "South Australia",
        " TAS": "Tasmania",
        " ACT": "the ACT",
        " NT": "the Northern Territory",
      };
      return { isInterstate: true, cityLabel: stateMap[state] ?? "your area" };
    }
  }
  return { isInterstate: false, cityLabel: "your area" };
}

// NOTE-FIX: the submit body carries TWO distinct fields — `card_message`
// (the recipient's card message, the hero) and `notes` (a small
// ops/logistics note). Each is trimmed + hard-capped at NOTES_MAX to
// match the server caps (submit-gift-request LENGTH_CAPS.card_message /
// .notes) so neither can trip the server-side 400. The backend writes
// card_message → orders.card_message (the clean recipient note) and
// notes → delivery_window.notes (ops) — they are never folded together.
const NOTES_MAX = 1000;

export function OrderConfirmSheet({
  sku,
  selectedAddons = [],
  recipientName,
  recipientId,
  occasion,
  defaultDate,
  defaultAddress,
  cameFrom,
  fulfillmentReturn = null,
  onClose,
  onSubmitted,
  isAnonymous = false,
  guestSessionId = null,
  chatSessionId = null,
  celebrationId = null,
  bulkCheckout = null,
}: OrderConfirmSheetProps) {
  const navigate = useNavigate();
  const profileQuery = useProfile();
  const companyId = profileQuery.data?.company_id ?? null;
  const closeRef = useRef<HTMLButtonElement>(null);
  const addonsTotalAud = selectedAddons.reduce((sum, a) => sum + a.price_aud, 0);
  const orderTotalAud = Number(sku.price_aud) + addonsTotalAud;
  const orderTotalCents = Math.round(orderTotalAud * 100);

  // Sprint: buyer must explicitly pick a delivery date. We only
  // pre-fill when the upstream surface passed a real date (linked
  // celebration, chat-resolved brief). Otherwise leave it blank so
  // canSubmit blocks until the buyer makes a real choice — the old
  // todayPlus(7) silent default produced surprise dates that the
  // buyer never consented to.
  const [deliveryDate, setDeliveryDate] = useState<string>(
    defaultDate && /^\d{4}-\d{2}-\d{2}$/.test(defaultDate) ? defaultDate : "",
  );
  // Sprint: occasion is editable in the sheet. Default to whatever
  // the upstream surface passed; the buyer can override before
  // submit. Submit-gift-request stores this as celebration_type.
  const [occasionInput, setOccasionInput] = useState<string>(
    () => normalizeOccasionForConfirm(occasion),
  );
  const [deliveryAddress, setDeliveryAddress] = useState<string>(defaultAddress ?? "");

  useEffect(() => {
    if (defaultDate && /^\d{4}-\d{2}-\d{2}$/.test(defaultDate)) {
      setDeliveryDate((prev) => prev || defaultDate);
    }
  }, [defaultDate]);

  useEffect(() => {
    if (defaultAddress?.trim()) {
      setDeliveryAddress((prev) => prev || defaultAddress.trim());
    }
  }, [defaultAddress]);

  useEffect(() => {
    setOccasionInput(normalizeOccasionForConfirm(occasion));
  }, [occasion]);
  // NOTE-FE: `notes` is now the FIRST-CLASS recipient card message —
  // the words the recipient actually reads. The send contract is
  // unchanged (still one `notes` string on the submit body, still the
  // existing send path); the backend dual-writes it to
  // orders.card_message. Kept as plain `notes` so the bind + payload
  // line below stay byte-identical to pre-NOTE-FE.
  const [notes, setNotes] = useState<string>("");
  // NOTE-FE: secondary, de-conflated logistics/allergy capture. The
  // hero note above is what the recipient sees; this smaller field is
  // for the Lembra fulfilment team (gate codes, level/desk, allergy
  // confirmations the dietary cross-check funnels here). We do NOT add
  // a new submit-body field for it — that would change the contract.
  // Instead it's folded into the existing `notes` string at submit
  // time behind a clear delimiter (see buildNotesPayload), landing in
  // orders.notes (the ops/fulfilment column) alongside the card
  // message. One field on the wire; two intents in the UI.
  const [logisticsNote, setLogisticsNote] = useState<string>("");
  // MS289: Google Places autocomplete on the delivery address input.
  // The hook returns a ref the input attaches to; on place_changed it
  // fires the callback with the formatted address. Falls back silently
  // to plain text entry if Maps fails to load. AU-restricted via the
  // hook's default country option.
  const addressRef = useAddressAutocomplete((parsed) => {
    setDeliveryAddress(parsed.formatted);
    void captureInteraction({
      surface: "order_confirm",
      event_type: "field_edited",
      payload: { field: "delivery_address", source: "google_places" },
      recipient_id: recipientId ?? null,
    });
  });
  // Recipient name can be pre-filled from props (came from chat /
  // browse with a recipient context), OR collected in this sheet
  // when the prior surfaces didn't capture it. Sprint 1.7c hits
  // this regularly because anon visitors don't have team_members.
  const [recipientNameInput, setRecipientNameInput] = useState<string>(recipientName ?? "");
  const recipientNameKnown = (recipientName ?? "").trim().length > 0;
  // NOTE-FE: first name for the hero note label ("Your note to Sarah").
  // Prefer whatever's live in the input (covers the anon path where the
  // buyer types the recipient just above), fall back to the prop, then
  // to a warm generic so the label never reads "Your note to ". Mirrors
  // the SavedState first-name split already used lower in this file.
  const recipientFirstName =
    (recipientNameInput.trim() || recipientName?.trim() || "").split(/\s+/)[0] || "";
  const noteLabelTarget = recipientFirstName || "them";
  // Sprint 1.7c anonymous-customer fields. Only used when isAnonymous.
  const [guestName, setGuestName] = useState<string>("");
  const [guestEmail, setGuestEmail] = useState<string>("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sprint A: promo code state for prospect-bound campaign codes.
  // Buyer types the code from Lucky's email reply; we hit
  // validate-promo-code for a peek-only check (returns discount math
  // without locking the code), surface the discount line in the
  // recap, and pass the code through to submit-gift-request which
  // re-validates + locks the redemption at order-creation time.
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promoState, setPromoState] = useState<
    "idle" | "validating" | "valid" | "invalid" | "error"
  >("idle");
  const [promoMessage, setPromoMessage] = useState<string | null>(null);
  const [promoDiscountCents, setPromoDiscountCents] = useState(0);

  // Sprint F: pull authed user email for the promo-validate call.
  // Anon users fall back to guestEmail (typed into this sheet).
  const { user: authUser } = useAuth();
  const effectivePromoEmail = (
    isAnonymous ? guestEmail.trim() : (authUser?.email ?? "")
  ).toLowerCase().trim();

  const handleValidatePromo = async () => {
    const code = promoInput.trim();
    if (code.length < 3) {
      setPromoState("invalid");
      setPromoMessage("Type the full code.");
      setPromoDiscountCents(0);
      return;
    }
    if (!effectivePromoEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(effectivePromoEmail)) {
      setPromoState("invalid");
      setPromoMessage(
        isAnonymous
          ? "Type your email above first. Promo codes are tied to a specific address."
          : "We couldn't read your email. Refresh the page and try again.",
      );
      setPromoDiscountCents(0);
      return;
    }
    setPromoState("validating");
    setPromoMessage(null);
    setPromoDiscountCents(0);
    try {
      const orderTotalCentsForPromo = orderTotalCents;
      const { data, error: invokeErr } = await supabase.functions.invoke(
        "validate-promo-code",
        {
          body: {
            code,
            order_total_cents: orderTotalCentsForPromo,
            customer_email: effectivePromoEmail,
          },
        },
      );
      type ValidateResp =
        | {
            ok: true;
            valid: true;
            discount_cents: number;
            total_after_cents: number;
            cap_cents: number | null;
            message: string;
          }
        | { ok: true; valid: false; reason: string; message: string }
        | { ok: false; error: string };
      const parsed = data as ValidateResp | null;
      if (invokeErr || !parsed) {
        setPromoState("error");
        setPromoMessage("Couldn't check the code. Try again in a moment.");
        return;
      }
      if (!parsed.ok) {
        setPromoState("error");
        setPromoMessage((parsed as { error: string }).error);
        return;
      }
      if (!parsed.valid) {
        setPromoState("invalid");
        setPromoMessage(parsed.message);
        return;
      }
      setPromoState("valid");
      setPromoMessage(parsed.message);
      setPromoDiscountCents(parsed.discount_cents);
      setPromoInput(code);
    } catch (e) {
      setPromoState("error");
      setPromoMessage(
        e instanceof Error
          ? e.message
          : "Couldn't check the code. Try again in a moment.",
      );
    }
  };

  // MS318: recipient dietary cross-check. When the order is for a
  // known team member (recipientId is set), fetch their
  // dietary_requirements + allergies and compare against the SKU's
  // dietary_tags. Surfaces a coral warning when:
  //   - SKU is consumable (cake / hamper / meal — vouchers, spa,
  //     workshops, florists don't have a dietary axis per the
  //     chat-concierge prompt rules)
  //   - Recipient has a dietary tag the SKU doesn't carry
  //   - Or recipient has an allergy and the SKU has no explicit
  //     allergen-free flag for it
  //
  // Warning is informational — doesn't block submit. The customer
  // may know more than the data (vendor will accommodate, recipient
  // just updated their prefs, etc.). Cuts down on the "I ordered
  // Sarah a regular cake but she's vegan" regret pattern.
  const isConsumable =
    sku.vendor_category === "cake" ||
    sku.vendor_category === "hamper" ||
    sku.vendor_category === "meal";
  const [recipientDietary, setRecipientDietary] = useState<{
    dietary_requirements: string[];
    allergies: string[];
  } | null>(null);
  useEffect(() => {
    if (!recipientId || !isConsumable) {
      setRecipientDietary(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("team_members")
        .select("dietary_requirements, allergies")
        .eq("id", recipientId)
        .maybeSingle();
      if (cancelled) return;
      setRecipientDietary({
        dietary_requirements: (data?.dietary_requirements as string[] | null) ?? [],
        allergies: (data?.allergies as string[] | null) ?? [],
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [recipientId, isConsumable]);
  // Derive the missing tags. SKU tags are lowercase snake_case post-
  // MS315; recipient tags can still be mixed case from older inputs,
  // so normalise both sides to lowercase for comparison.
  const skuTagsLower = new Set(
    (sku as { dietary_tags?: string[] }).dietary_tags?.map((t) => String(t).toLowerCase()) ?? [],
  );
  const recipientTagsLower = (recipientDietary?.dietary_requirements ?? []).map((t) =>
    String(t).toLowerCase().replace(/-/g, "_"),
  );
  const missingDietaryTags = isConsumable
    ? recipientTagsLower.filter((t) => !skuTagsLower.has(t))
    : [];
  // Allergies aren't tagged on SKUs today (vendor_products doesn't
  // have an allergens column). Surface the recipient's known
  // allergies as a flag-for-the-customer-to-verify rather than a
  // hard block.
  const recipientAllergies = recipientDietary?.allergies ?? [];

  // MS312: lead-time validation. Without this, a customer can pick
  // tomorrow for a SKU whose vendor needs 5 days to prepare — the
  // order reaches Stripe, payment is taken, then the vendor scrambles
  // (or Lembra ops manually refunds + cancels). Lucky's audit pattern
  // for "the order arrived late" was traceable to exactly this gap.
  //
  // earliestDate = today + lead_time_days. Treat null/0 as same-day
  // (no constraint beyond the existing todayPlus(1) tomorrow-floor).
  // The input's `min` attribute enforces in the browser date picker,
  // canSubmit prevents the submit button from firing on stale state,
  // and a warning surfaces below the input so the customer sees
  // WHY their choice is gated.
  //
  // Note: vouchers (is_voucher=true) are typically same-day delivery
  // (email/digital code), so they get no extra lead-time constraint.
  // Physical SKUs (cakes, flowers, hampers) inherit their lead_time_days.
  const leadTimeDays = sku.is_voucher
    ? 0
    : (typeof sku.lead_time_days === "number" && sku.lead_time_days > 0
        ? sku.lead_time_days
        : 0);
  const earliestDate = todayPlus(Math.max(1, leadTimeDays));
  const deliveryDateTooSoon =
    deliveryDate.length === 10 && deliveryDate < earliestDate;

  // MS335: vendor-blocked-date upfront warning. MS324 already enforces
  // this server-side, but the customer only learns at submit-time
  // (after they've typed in their address, notes, etc.). Fetch the
  // vendor's blocked dates on mount and surface a coral hint the
  // moment the customer picks one, matching the lead-time hint
  // pattern (MS312).
  //
  // We look up the vendor_partner_id from the SKU rather than adding
  // it to SkuCardData — keeps the card payload lean across every
  // hydration surface (Browse, chat, wishlist, vendor pages). The
  // extra round-trip happens once when the sheet opens, before the
  // customer has finished typing.
  //
  // RPC: get_vendor_unavailable_dates (MS335 migration) is SECURITY
  // DEFINER + GRANTed to anon/authenticated so anonymous + authed
  // customers both hit it. Returns dates only (no reason) so the
  // table's internal reason field stays vendor-private.
  const [blockedDates, setBlockedDates] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!sku.sku_id) return;
    let cancelled = false;
    (async () => {
      // Step 1: resolve the SKU's vendor_partner_id. Vouchers are
      // typically same-vendor SKUs too; we still want to gate them
      // even though they're same-day (lead_time_days=0 → earliestDate
      // is today+1) because a vendor can block specific dates for any
      // reason (Public holiday for their fulfilment team, system
      // maintenance window, etc.).
      const { data: productRow } = await supabase
        .from("vendor_products")
        .select("vendor_partner_id")
        .eq("id", sku.sku_id)
        .maybeSingle();
      if (cancelled) return;
      const vendorId = (productRow as { vendor_partner_id?: string } | null)
        ?.vendor_partner_id;
      if (!vendorId) {
        setBlockedDates(new Set());
        return;
      }
      // Step 2: ask the RPC for blocked dates in the next 90 days.
      // OrderConfirmSheet defaults to today+7 and customers rarely
      // pick further than a month out; 90 days covers seasonal blocks
      // (vendor closes for Easter / public holiday) with headroom.
      const fromIso = todayPlus(0);
      const toIso = todayPlus(90);
      const { data: dates } = await supabase.rpc(
        "get_vendor_unavailable_dates",
        { p_vendor_id: vendorId, p_from: fromIso, p_to: toIso },
      );
      if (cancelled) return;
      // RPC returns SETOF date → supabase-js gives us an array of
      // ISO strings. Defensive: tolerate either {} array of strings
      // or null.
      const dateStrings = Array.isArray(dates)
        ? dates.map((d) => (typeof d === "string" ? d : String(d)))
        : [];
      setBlockedDates(new Set(dateStrings));
    })();
    return () => {
      cancelled = true;
    };
  }, [sku.sku_id]);
  const deliveryDateBlocked =
    deliveryDate.length === 10 && blockedDates.has(deliveryDate);
  // For the helper hint we want the next 1-3 upcoming blocked dates
  // within the customer's likely picking window so they can dodge
  // them upfront rather than playing whack-a-mole. Sort + filter to
  // dates >= today.
  const upcomingBlockedDates = Array.from(blockedDates)
    .filter((d) => d >= todayPlus(0))
    .sort()
    .slice(0, 3);
  // MS339: when the picked date is blocked, compute the NEXT available
  // date (after earliestDate, skipping any blocked dates) so we can
  // suggest it inline with a one-click apply button. Pre-fix the
  // customer had to manually open the picker, guess a different date,
  // and hope it wasn't also blocked. The native date input doesn't let
  // us disable specific dates inside the calendar popup, so the next-
  // available suggestion is the lightest-touch recovery path.
  const nextAvailableDate = (() => {
    if (!deliveryDateBlocked) return null;
    // Walk forward from the picked date one day at a time. Cap at 30
    // days so we don't loop forever on a fully-blocked vendor (shouldn't
    // happen in practice but defensive). 30 covers any realistic
    // multi-week vendor outage.
    const start = new Date(`${deliveryDate}T00:00:00`);
    for (let offset = 1; offset <= 30; offset++) {
      const next = new Date(start);
      next.setDate(next.getDate() + offset);
      const iso = next.toISOString().slice(0, 10);
      // Skip dates earlier than the vendor's lead-time floor (rare but
      // possible if the customer typed an unusually-early blocked date)
      if (iso < earliestDate) continue;
      if (!blockedDates.has(iso)) return iso;
    }
    return null;
  })();
  // MS355: interstate-address detection state. Triggers when the
  // delivery_address contains a non-VIC state code or non-Melbourne
  // capital city name. The submit button is replaced with a capture
  // CTA in that case so we log the interest instead of taking an
  // order we can't fulfil.
  const interstate = detectInterstate(deliveryAddress);
  // Distinct flow state from the standard submit. Keeps the request
  // capture UX separate from the "redirecting to Stripe" path.
  const [interstateRequestState, setInterstateRequestState] = useState<
    "idle" | "submitting" | "submitted" | "error"
  >("idle");
  const [interstateRequestError, setInterstateRequestError] = useState<string | null>(null);
  const [interstateContactEmail, setInterstateContactEmail] = useState<string>("");
  const [interstateNotes, setInterstateNotes] = useState<string>("");

  // Sprint 1.7b: the success state now represents a "Stripe is loading"
  // moment, submit returns a checkout_url that we redirect the user
  // to. This state pins the sheet open with a "Taking you to checkout…"
  // message in case the redirect is slow or the browser blocks it.
  const [redirectingTo, setRedirectingTo] = useState<string | null>(null);
  // When Stripe is unavailable but the request was saved, we surface
  // a recovery state with the order reference so the customer has
  // peace of mind even though we couldn't show them a payment link.
  const [recoveryRef, setRecoveryRef] = useState<string | null>(null);

  // MS479: live refs so the ESC keydown handler (registered once at
  // mount with an empty dep array) always reads the current blocking
  // flags rather than the stale mount-time values. Without these, any
  // press of Escape while a Stripe redirect is in flight would dismiss
  // the sheet before location.assign completes — the buyer loses the
  // checkout URL and the order sits at payment_status=processing
  // forever. Using refs avoids re-registering the listener on every
  // state change (which would create a flash of un-guarded ESC between
  // the remove + re-add calls).
  const submittingLiveRef = useRef(false);
  submittingLiveRef.current = submitting;
  const redirectingLiveRef = useRef<string | null>(null);
  redirectingLiveRef.current = redirectingTo;

  // Focus close button on open, handle ESC, lock body scroll
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submittingLiveRef.current && !redirectingLiveRef.current) onClose();
    };
    window.addEventListener("keydown", onKey);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    void captureInteraction({
      surface: "order_confirm",
      event_type: "sheet_opened",
      payload: { sku_id: sku.sku_id, came_from: cameFrom },
      recipient_id: recipientId ?? null,
      vendor_product_id: sku.sku_id,
    });
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = originalOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Email validation is light here, Stripe re-validates on checkout
  // and the edge function does its own regex check.
  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail.trim());

  const canSubmit =
    deliveryDate.length === 10 &&
    !deliveryDateTooSoon &&
    // MS335: block submit if the vendor marked this date unavailable.
    // Pairs with the inline coral warning under the date input.
    !deliveryDateBlocked &&
    deliveryAddress.trim().length > 4 &&
    // MS355: block standard submit when the address is interstate.
    // The capture CTA below replaces the submit button in that case
    // so we log the demand signal instead of taking an order we
    // can't fulfil.
    !interstate.isInterstate &&
    recipientNameInput.trim().length >= 2 &&
    (!isAnonymous || (guestName.trim().length >= 2 && emailLooksValid)) &&
    !submitting &&
    !redirectingTo &&
    !recoveryRef;

  // MS355: handler for the interstate-interest capture CTA. Calls the
  // submit-interstate-interest edge function which lands a row in the
  // remote_celebration_requests table (same table the chat-concierge
  // uses via submit_remote_request) — one source of truth for "where
  // is unmet demand?".
  const handleInterstateRequest = async () => {
    if (interstateRequestState === "submitting") return;
    // Determine the email to send. Authed users get the profile email
    // from the guest field if they typed one; otherwise the inline
    // capture field. Anon users use the guest_email they typed
    // earlier, or the inline field if blank.
    const candidateEmail =
      interstateContactEmail.trim() ||
      (isAnonymous ? guestEmail.trim() : "");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidateEmail)) {
      setInterstateRequestError("We need a valid email to let you know when we go live.");
      return;
    }
    setInterstateRequestState("submitting");
    setInterstateRequestError(null);
    try {
      const { data, error } = await supabase.functions.invoke(
        "submit-interstate-interest",
        {
          body: {
            recipient_location: deliveryAddress.trim() || interstate.cityLabel,
            contact_email: candidateEmail,
            brief_summary: interstateNotes.trim().length > 0
              ? `Interstate request for ${interstate.cityLabel} (${sku.vendor_category}): ${interstateNotes.trim()}`
              : `Interstate request for ${interstate.cityLabel} (${sku.vendor_category}, SKU "${sku.name}", $${sku.price_aud}).`,
            vendor_category: sku.vendor_category,
          },
        },
      );
      type InterestResponse = { ok: boolean; request_id?: string; error?: string };
      const parsed = data as InterestResponse | null;
      if (error || !parsed?.ok) {
        // MS476: supabase-js FunctionsHttpError sets data=null and puts
        // the response body on error.context. Extract the real server
        // error message from there; fall back to a generic string only
        // when the body is not parseable (network error, relay error).
        let errMsg = parsed?.error ?? null;
        let ctxStatus: number | null = null;
        if (!errMsg && error && "context" in error) {
          try {
            const ctx = await (error.context as Response).json() as InterestResponse;
            errMsg = ctx?.error ?? null;
            ctxStatus = (error.context as Response).status ?? null;
          } catch { /* non-JSON body — use fallback below */ }
        }
        // MS489: the function works correctly when called directly (curl
        // → 200 OK, row inserted) but Lucky reported a "couldn't log
        // your request" warning. Zero successful submissions in
        // remote_celebration_requests prior to MS489 = nobody ever
        // got past this branch. Capture the real failure so the next
        // attempt surfaces details we can act on instead of the
        // generic copy. Console.error helps when debugging from
        // dev tools; Sentry catches prod failures the user doesn't
        // open the console for.
        console.error("[interstate-interest] submit failed", {
          error,
          parsed,
          ctxStatus,
          requestBody: {
            recipient_location: deliveryAddress.trim() || interstate.cityLabel,
            vendor_category: sku.vendor_category,
            email_provided: candidateEmail.length > 0,
          },
        });
        Sentry.captureMessage("interstate-interest submit failed", {
          level: "error",
          tags: {
            scope: "order_confirm_interstate",
            vendor_category: sku.vendor_category,
            ctx_status: ctxStatus ?? undefined,
          },
          extra: {
            errorName: error?.name,
            errorMessage: error?.message,
            parsedError: parsed?.error,
            cityLabel: interstate.cityLabel,
          },
        });
        setInterstateRequestState("error");
        setInterstateRequestError(
          errMsg ??
            "We couldn't log your request right now. Try once more, or email hello@lembra.com.au.",
        );
        return;
      }
      setInterstateRequestState("submitted");
      void captureInteraction({
        surface: "order_confirm",
        event_type: "interstate_interest_logged",
        payload: {
          city: interstate.cityLabel,
          vendor_category: sku.vendor_category,
          request_id: parsed.request_id ?? null,
        },
        recipient_id: recipientId ?? null,
      });
    } catch (e) {
      setInterstateRequestState("error");
      setInterstateRequestError(
        e instanceof Error
          ? e.message
          : "Something went wrong. Try once more, or email hello@lembra.com.au.",
      );
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    try {
      // MS200: silent retry on cold-start. Same pattern as MS198/199
      // — but applied to the conversion-critical submit-gift-request
      // function. Losing an order to a cold-start network blip is the
      // worst-case failure mode on the platform.
      //
      // Safety guard: only retry when `error && !data` (network-shape
      // failure — function never started executing). Server-returned
      // errors get surfaced unchanged so we DON'T retry on a function
      // that may have already created a partial DB row (the function
      // is idempotent at the rec+order level via the request_ref, but
      // we shouldn't rely on that for retry semantics).
      const body = {
        sku_id: sku.sku_id,
        recipient_id: recipientId ?? undefined,
        recipient_name: recipientNameInput.trim(),
        occasion: occasionInput,
        delivery_date: deliveryDate,
        delivery_address: deliveryAddress.trim(),
        // NOTE-FIX: two distinct fields. `card_message` is the recipient's
        // card message (the hero) → orders.card_message; `notes` is the
        // optional ops/logistics note → delivery_window.notes. Each is
        // trimmed + capped at NOTES_MAX; empty → undefined so the field is
        // omitted (matching the pre-note behaviour for an empty note).
        card_message: notes.trim().slice(0, NOTES_MAX) || undefined,
        notes: logisticsNote.trim().slice(0, NOTES_MAX) || undefined,
        came_from: cameFrom,
        // Sprint 1.7p, only sent from chat surface; browse leaves null.
        chat_session_id: chatSessionId ?? undefined,
        // Sprint 2.5a /orders-new-redirect follow-up, links the
        // resulting order back to the source celebration row when
        // the chat was entered from a calendar/notification
        // approval. submit-gift-request already accepts this field.
        // One-off delivery override: when celebrationId is set,
        // delivery_date applies only to this order's delivery_window —
        // submit-gift-request does NOT update celebrations.date.
        celebration_id: celebrationId ?? undefined,
        ...(bulkCheckout
          ? {
              bulk_mode: true,
              bulk_period_key: bulkCheckout.bulkPeriodKey,
              bulk_run_id: bulkCheckout.bulkRunId,
              celebration_ids: bulkCheckout.celebrationIds,
            }
          : {}),
        // Sprint 1.7c: only sent when the visitor is anonymous.
        // Authed callers leave these undefined and the edge fn
        // sources customer email/name from the profile row.
        ...(isAnonymous
          ? {
              guest_email: guestEmail.trim(),
              guest_name: guestName.trim(),
              guest_session_id: guestSessionId ?? undefined,
            }
          : {}),
        // Sprint A: pass the validated promo code through. The edge
        // fn re-validates server-side + atomically locks the
        // redemption to this order id, so a stale "valid" state on
        // the client can't double-redeem.
        ...(promoState === "valid" && promoInput.trim()
          ? { promo_code: promoInput.trim() }
          : {}),
        ...(selectedAddons.length > 0
          ? { addon_sku_ids: selectedAddons.map((a) => a.sku_id) }
          : {}),
      };

      let invokeResult = await supabase.functions.invoke(
        "submit-gift-request",
        { body },
      );

      if (invokeResult.error && !invokeResult.data) {
        await new Promise((r) => setTimeout(r, 800));
        invokeResult = await supabase.functions.invoke(
          "submit-gift-request",
          { body },
        );
        if (!invokeResult.error && invokeResult.data) {
          void captureInteraction({
            surface: "order_confirm",
            event_type: "submit_cold_start_recovered",
            payload: { sku_id: sku.sku_id, came_from: cameFrom },
            recipient_id: recipientId ?? null,
            vendor_product_id: sku.sku_id,
          });
        }
      }

      const { data, error: invokeError } = invokeResult;
      if (invokeError) throw new Error(invokeError.message);

      // Stripe-unavailable recovery path: the function may return a
      // recovery payload when the rec + order rows exist but the
      // Stripe session couldn't be created. We tell the customer
      // their request is safe and Lembra will follow up.
      if (data && !data.ok && data?.recovery === "saved_no_stripe") {
        setRecoveryRef(data.order_reference ?? data.request_ref ?? "");
        void captureInteraction({
          surface: "order_confirm",
          event_type: "stripe_unavailable_saved",
          payload: { sku_id: sku.sku_id, request_ref: data.request_ref },
          recipient_id: recipientId ?? null,
          vendor_product_id: sku.sku_id,
        });
        return;
      }
      // MS302: surface the server's specific error message when one is
      // present, instead of letting the generic catch-block fallback
      // ("We couldn't take you to checkout just now…") swallow it. The
      // server returns actionable, user-friendly messages in `data.error`
      // for known failure modes:
      //   - MS293 vendor-paused SKU: "We couldn't find that gift. It may
      //     have just been retired — pick another?"
      //   - MS128 cross-tenant celebration_id: "That celebration isn't on
      //     your account. Refresh and try again."
      //   - MS145 length-cap breaches: "notes too long (max 1000 chars)"
      //   - MS144 delivery-date validation: "delivery_date can't be in the past"
      // Each of these is more useful than the generic fallback. Only
      // network-shape failures (caught at line 308 below) get the
      // generic retry copy.
      if (!data?.ok) {
        setError(data?.error ?? "Submit failed. Try again in a moment.");
        void captureInteraction({
          surface: "order_confirm",
          event_type: "submit_failed",
          payload: { sku_id: sku.sku_id, error: data?.error ?? "no_error_field", server_error: true },
          recipient_id: recipientId ?? null,
          vendor_product_id: sku.sku_id,
        });
        return;
      }
      const orderId = data.order_id as string | undefined;
      const checkoutUrl = data.checkout_url as string | undefined;
      if (!checkoutUrl) throw new Error("No checkout URL returned");

      void captureInteraction({
        surface: "order_confirm",
        event_type: "gift_request_submitted",
        payload: {
          sku_id: sku.sku_id,
          request_id: data.request_id,
          request_ref: data.request_ref,
          order_reference: data.order_reference,
          vendor_category: sku.vendor_category,
          came_from: cameFrom,
        },
        recipient_id: recipientId ?? null,
        vendor_product_id: sku.sku_id,
      });
      onSubmitted?.(data.request_id);

      if (!isAnonymous && companyId && orderId) {
        const lineItems: ChargeSavedCardLineItem[] = [
          {
            name: formatSkuName({
              name: sku.name,
              vendor_name: sku.vendor_name,
              vendor_category: sku.vendor_category,
              is_voucher: sku.is_voucher,
            }),
            amount: orderTotalAud,
          },
        ];
        const charge = await chargeSavedCardForOrder({
          companyId,
          orderId,
          amountAud: orderTotalAud,
          description: `${occasionInput} for ${recipientNameInput.trim() || recipientName}`,
          lineItems,
        });
        if (charge.kind === "paid") {
          navigate(`/orders/${orderId}/celebrate`);
          onClose();
          return;
        }
        if (charge.kind === "failed") {
          setError(charge.message);
          return;
        }
      }

      // Sprint 1.7b: redirect to Stripe Checkout when no saved card or
      // charge requires checkout. Sheet stays open with a redirect hint.
      setRedirectingTo(checkoutUrl);
      window.location.assign(checkoutUrl);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError("We couldn't take you to checkout just now. Try again in a moment.");
      void captureInteraction({
        surface: "order_confirm",
        event_type: "submit_failed",
        payload: { sku_id: sku.sku_id, error: msg },
        recipient_id: recipientId ?? null,
        vendor_product_id: sku.sku_id,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (submitting || redirectingTo) return;
    if (!recoveryRef) {
      void captureInteraction({
        surface: "order_confirm",
        event_type: "cancelled",
        payload: { sku_id: sku.sku_id, came_from: cameFrom },
        recipient_id: recipientId ?? null,
        vendor_product_id: sku.sku_id,
      });
    }
    onClose();
  };

  // Render into document.body via portal so that any CSS transform or
  // overflow:hidden on a parent component cannot clip or reposition the modal.
  // position:fixed is always relative to the initial containing block (viewport)
  // UNLESS a transform ancestor exists — portaling sidesteps that entirely.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Confirm your gift"
      className="fixed inset-0 z-50 flex items-end justify-center bg-navy/40 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={(e) => {
        // Click on the backdrop closes, but only if we're not mid-submit
        // and not in recovery state (where the user needs to note their
        // order reference before closing). MS403: add recoveryRef guard.
        if (e.target === e.currentTarget && !submitting && !recoveryRef) handleCancel();
      }}
    >
      <div
        className="w-full max-w-md flex flex-col rounded-t-2xl bg-white shadow-xl overflow-hidden sm:rounded-2xl"
        style={{ fontFamily: "DM Sans, sans-serif", maxHeight: "95dvh" }}
      >
        {/* Header */}
        <div className="flex shrink-0 flex-col gap-2 border-b border-charcoal/10 px-5 py-3.5">
          {fulfillmentReturn && !submitting && !redirectingTo && !recoveryRef && (
            <FulfillmentReturnLink
              context={{
                ...fulfillmentReturn,
                onReturn: () => {
                  fulfillmentReturn.onReturn();
                  handleCancel();
                },
              }}
            />
          )}
          <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-navy">
            {recoveryRef ? "Saved" : (submitting || redirectingTo) ? "Going to checkout" : "Confirming gift"}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={handleCancel}
            disabled={submitting || !!redirectingTo}
            aria-label="Close"
            /* MS203: was h-8 w-8 (32px). Mid-flow close on the order-
               confirm sheet — high-traffic on mobile. Same 44px tap-
               target bump as MS201 applied to RecipientContextHeader's
               back button. Icon stays at 18px; only the hit area
               grows. Sheet header row has py-3.5 so the larger
               button fits without forcing a layout reflow. */
            className="inline-flex h-11 w-11 items-center justify-center rounded-full text-charcoal/70 hover:bg-charcoal/8 hover:text-navy disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-coral"
          >
            <X size={18} strokeWidth={2.5} />
          </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
        {recoveryRef ? (
          <SavedState
            recipientName={recipientName}
            orderReference={recoveryRef}
            fulfillmentReturn={fulfillmentReturn}
            onClose={onClose}
          />
        ) : (submitting || redirectingTo) ? (
          // MS406: unified checkout-in-progress panel — shown from first button click
          // through navigation. Replaces the old pattern where the form stayed visible
          // for the ~10-second edge function call (with only a small button spinner),
          // then briefly flashed RedirectingState before window.location.assign fired.
          // One clean transition instead of two.
          <CheckoutProgressState checkoutUrl={redirectingTo} />
        ) : (
          <div className="space-y-5 px-5 py-5">
            {/* SKU recap */}
            <div className="flex gap-3 rounded-2xl border border-charcoal/10 bg-butter/20 p-3">
              <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-charcoal/5">
                {sku.image_url ? (
                  <img
                    src={sku.image_url}
                    alt={`${sku.name}, ${sku.vendor_category} from ${sku.vendor_name}`}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-butter to-butter/40 text-[10px] uppercase tracking-wider text-navy/60">
                    {sku.vendor_category}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-charcoal/55">{sku.vendor_name}</p>
                {/* 2026-05-16 UX-copy audit: dedupe vendor / "Voucher"
                    / "$N" tokens via the shared formatter so the
                    order sheet's title line matches the card + modal. */}
                <p className="truncate text-sm font-semibold text-navy">
                  {formatSkuName({
                    name: sku.name,
                    vendor_name: sku.vendor_name,
                    vendor_category: sku.vendor_category,
                    is_voucher: sku.is_voucher,
                  })}
                </p>
                <p className="mt-0.5 text-sm font-semibold text-navy">
                  {formatSkuPrice(sku.price_aud, sku.is_voucher)}
                </p>
                {selectedAddons.length > 0 && (
                  <ul className="mt-2 space-y-1 border-t border-charcoal/10 pt-2">
                    {selectedAddons.map((addon) => (
                      <li
                        key={addon.sku_id}
                        className="flex items-center justify-between gap-2 text-xs text-charcoal/75"
                      >
                        <span className="truncate">+ {addon.name}</span>
                        <span className="shrink-0 font-medium text-navy">
                          {formatSkuPrice(addon.price_aud, addon.is_voucher)}
                        </span>
                      </li>
                    ))}
                    <li className="flex items-center justify-between gap-2 text-sm font-semibold text-navy pt-1">
                      <span>Total</span>
                      <span>{formatSkuPrice(orderTotalAud, sku.is_voucher)}</span>
                    </li>
                  </ul>
                )}
                {/* NOTE-FE: lightweight preview of the recipient card
                    message in the recap, so the buyer sees their words
                    travel with the gift. Card message only (never the
                    logistics note). Renders only once a note exists, so
                    the recap stays lean for the empty case. Sits below
                    the add-on recap without altering it. */}
                {notes.trim() && (
                  <p className="mt-2 border-t border-charcoal/10 pt-2 text-xs italic text-charcoal/70">
                    <span className="not-italic font-medium text-charcoal/55">Note: </span>
                    &ldquo;{notes.trim()}&rdquo;
                  </p>
                )}
              </div>
            </div>

            {/* Recipient: read-only when pre-filled, input when the
                prior surface didn't capture it (anon visitors, or
                authed visitors who came in with no recipient context). */}
            {recipientNameKnown ? (
              <div className="space-y-1 text-sm">
                <p className="flex justify-between gap-3">
                  <span className="text-charcoal/60">For</span>
                  <span className="text-right font-medium text-navy">{recipientNameInput}</span>
                </p>
                <div className="flex items-center justify-between gap-3">
                  <label htmlFor="confirm-occasion-1" className="text-charcoal/60">
                    Occasion
                  </label>
                  <select
                    id="confirm-occasion-1"
                    value={occasionInput}
                    onChange={(e) => setOccasionInput(e.target.value)}
                    className="rounded-lg border-2 border-charcoal/15 bg-white px-2 py-1 text-sm font-medium text-navy outline-none focus:border-coral"
                  >
                    <option value="birthday">Birthday</option>
                    <option value="work_anniversary">Work anniversary</option>
                    <option value="welcome">Welcome</option>
                    <option value="farewell">Farewell</option>
                    <option value="milestone">Milestone</option>
                    <option value="thank_you">Thank you</option>
                    <option value="congratulations">Congratulations</option>
                    <option value="just_because">Just because</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                {/* MS318: recipient dietary cross-check warning. Surfaces
                    when the chosen SKU misses dietary tags the recipient
                    is known to need, OR when the recipient has known
                    allergies (since SKUs don't carry an allergen
                    column today). Informational — doesn't block submit
                    because the customer may know more than the data
                    (vendor will accommodate, recipient prefs just
                    updated, etc.). */}
                {(missingDietaryTags.length > 0 || recipientAllergies.length > 0) && (
                  <div
                    role="alert"
                    className="mt-2 flex items-start gap-2 rounded-xl border border-coral/40 bg-coral/5 px-3 py-2 text-xs text-charcoal/80"
                  >
                    <AlertTriangle size={14} className="mt-0.5 shrink-0 text-coral" />
                    <div>
                      <p className="font-semibold text-coral">
                        {recipientNameInput}'s dietary worth checking:
                      </p>
                      {missingDietaryTags.length > 0 && (
                        <p className="mt-0.5">
                          Needs <span className="font-medium">{missingDietaryTags.join(", ").replace(/_/g, "-")}</span>. This SKU isn't tagged for it. The vendor may still accommodate; add a note under "For the Lembra team" below.
                        </p>
                      )}
                      {recipientAllergies.length > 0 && (
                        <p className="mt-0.5">
                          Allergies on file: <span className="font-medium">{recipientAllergies.join(", ")}</span>. Add these under "For the Lembra team" below so the vendor can confirm.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div>
                  <label
                    htmlFor="confirm-recipient"
                    className="mb-1 block text-xs font-semibold uppercase tracking-wider text-navy/70"
                    style={{ letterSpacing: "0.08em" }}
                  >
                    Who's this for?
                  </label>
                  <input
                    id="confirm-recipient"
                    type="text"
                    value={recipientNameInput}
                    onChange={(e) => setRecipientNameInput(e.target.value)}
                    placeholder="Recipient's first name"
                    autoComplete="off"
                    /* MS158: mirror the MS145 server-side cap (200 chars
                       on recipient_name). Without this the user could
                       type 10MB and hit a confusing server error on
                       submit; with it the browser caps the input
                       instantly. */
                    maxLength={200}
                    className="w-full rounded-xl border-2 border-charcoal/15 bg-white px-3 py-2 text-base text-navy outline-none placeholder:text-charcoal/45 focus:border-coral"
                  />
                </div>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <label htmlFor="confirm-occasion-2" className="text-charcoal/60">
                    Occasion
                  </label>
                  <select
                    id="confirm-occasion-2"
                    value={occasionInput}
                    onChange={(e) => setOccasionInput(e.target.value)}
                    className="rounded-lg border-2 border-charcoal/15 bg-white px-2 py-1 text-sm font-medium text-navy outline-none focus:border-coral"
                  >
                    <option value="birthday">Birthday</option>
                    <option value="work_anniversary">Work anniversary</option>
                    <option value="welcome">Welcome</option>
                    <option value="farewell">Farewell</option>
                    <option value="milestone">Milestone</option>
                    <option value="thank_you">Thank you</option>
                    <option value="congratulations">Congratulations</option>
                    <option value="just_because">Just because</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </>
            )}

            {/* Anonymous-visitor identity fields, only shown when not
                authed. Lembra needs both to send the confirmation
                email + invoice. Stripe also pre-fills its checkout
                with the email so the visitor doesn't re-type it. */}
            {isAnonymous && (
              <div className="space-y-3 rounded-2xl border border-charcoal/10 bg-butter/15 p-3.5">
                <p className="text-xs text-charcoal/65">A few details so we can send your confirmation.</p>
                <div>
                  <label
                    htmlFor="confirm-name"
                    className="mb-1 block text-xs font-semibold uppercase tracking-wider text-navy/70"
                    style={{ letterSpacing: "0.08em" }}
                  >
                    Your name
                  </label>
                  <input
                    id="confirm-name"
                    type="text"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder="e.g. Sarah Lee"
                    autoComplete="name"
                    /* MS158: 200-char cap, same convention as the
                       admin-create-company invitee_full_name field. */
                    maxLength={200}
                    className="w-full rounded-xl border-2 border-charcoal/15 bg-white px-3 py-2 text-base text-navy outline-none placeholder:text-charcoal/45 focus:border-coral"
                  />
                </div>
                <div>
                  <label
                    htmlFor="confirm-email"
                    className="mb-1 block text-xs font-semibold uppercase tracking-wider text-navy/70"
                    style={{ letterSpacing: "0.08em" }}
                  >
                    Email
                  </label>
                  <input
                    id="confirm-email"
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    placeholder="you@company.com.au"
                    autoComplete="email"
                    inputMode="email"
                    /* MS158: RFC 5321 max email length (320 chars). */
                    maxLength={320}
                    className="w-full rounded-xl border-2 border-charcoal/15 bg-white px-3 py-2 text-base text-navy outline-none placeholder:text-charcoal/45 focus:border-coral"
                  />
                </div>
              </div>
            )}

            {/* Delivery date */}
            <div>
              <label
                htmlFor="confirm-date"
                className="mb-1 block text-xs font-semibold uppercase tracking-wider text-navy/70"
                style={{ letterSpacing: "0.08em" }}
              >
                Delivery date
              </label>
              <input
                id="confirm-date"
                type="date"
                value={deliveryDate}
                onChange={(e) => {
                  setDeliveryDate(e.target.value);
                  void captureInteraction({
                    surface: "order_confirm",
                    event_type: "field_edited",
                    payload: { field: "delivery_date" },
                    recipient_id: recipientId ?? null,
                  });
                }}
                // MS312: enforce the SKU's lead_time_days via the picker's
                // `min` attribute so the browser disables earlier dates
                // in the calendar dropdown. canSubmit covers the case
                // where the customer types a date directly (some date
                // inputs allow that and skip the picker constraint).
                min={earliestDate}
                className={`w-full rounded-xl border-2 bg-white px-3 py-2 text-base text-navy outline-none focus:border-coral ${
                  deliveryDateTooSoon || deliveryDateBlocked
                    ? "border-coral"
                    : "border-charcoal/15"
                }`}
                aria-invalid={deliveryDateTooSoon || deliveryDateBlocked}
                aria-describedby={
                  deliveryDateTooSoon || deliveryDateBlocked
                    ? "confirm-date-hint"
                    : undefined
                }
              />
              {/* MS312: visible hint when the chosen date is too soon for
                  the vendor's lead time. Tells the customer the WHY
                  (not just "you can't pick that") so they don't blame
                  the picker. */}
              {deliveryDateTooSoon && (
                <p
                  id="confirm-date-hint"
                  className="mt-1.5 text-xs text-coral"
                  role="alert"
                >
                  {sku.vendor_name} needs {leadTimeDays} day{leadTimeDays === 1 ? "" : "s"} to prepare this. Earliest delivery is {earliestDate}.
                </p>
              )}
              {/* MS335: visible hint when the chosen date is one the
                  vendor explicitly blocked in their portal. Matches the
                  MS324 server-side gate so the customer sees the
                  problem in the picker instead of at submit.
                  MS339: surfaces a one-click jump to the next-available
                  date so the customer doesn't have to fish around in
                  the picker hunting for a working day. */}
              {!deliveryDateTooSoon && deliveryDateBlocked && (
                <div id="confirm-date-hint" className="mt-1.5" role="alert">
                  <p className="text-xs text-coral">
                    {sku.vendor_name} can't deliver on {deliveryDate}.
                    {nextAvailableDate && (
                      <>
                        {" "}Earliest available is {nextAvailableDate}.
                      </>
                    )}
                  </p>
                  {nextAvailableDate && (
                    <button
                      type="button"
                      onClick={() => {
                        setDeliveryDate(nextAvailableDate);
                        void captureInteraction({
                          surface: "order_confirm",
                          event_type: "field_edited",
                          payload: {
                            field: "delivery_date",
                            source: "blocked_date_suggest",
                          },
                          recipient_id: recipientId ?? null,
                        });
                      }}
                      className="mt-1 text-xs font-semibold text-navy underline-offset-2 hover:underline"
                    >
                      Use {nextAvailableDate} instead
                    </button>
                  )}
                </div>
              )}
              {/* Static lead-time note for SKUs that need 2+ days, even
                  when the customer hasn't picked a too-soon date yet.
                  Sets expectations upfront so they don't waste a click. */}
              {!deliveryDateTooSoon && !deliveryDateBlocked && leadTimeDays >= 2 && (
                <p className="mt-1.5 text-xs text-charcoal/55">
                  Vendor needs {leadTimeDays} day{leadTimeDays === 1 ? "" : "s"} to prepare — earliest is {earliestDate}.
                </p>
              )}
              {/* MS335: forward-look at the next few blocked dates so
                  the customer can dodge them before they pick. Only
                  shows when there ARE upcoming blocks AND the customer
                  hasn't already picked a problematic date (in which
                  case the coral hints above carry the load). */}
              {!deliveryDateTooSoon && !deliveryDateBlocked && upcomingBlockedDates.length > 0 && (
                <p className="mt-1.5 text-xs text-charcoal/55">
                  Heads up — {sku.vendor_name} is unavailable on{" "}
                  {upcomingBlockedDates.join(", ")}.
                </p>
              )}
            </div>

            {/* Delivery address */}
            <div>
              <label
                htmlFor="confirm-address"
                className="mb-1 block text-xs font-semibold uppercase tracking-wider text-navy/70"
                style={{ letterSpacing: "0.08em" }}
              >
                Delivery address
              </label>
              {/* MS289: switched from <textarea rows={2}> to a single-
                  line <input> with Google Places autocomplete via the
                  useAddressAutocomplete hook. The autocomplete needs a
                  single-line input to render its dropdown correctly.
                  Long addresses (unit/level/etc) still fit fine — the
                  300-char cap is preserved and the input scrolls
                  horizontally if needed. Unit/floor info that doesn't
                  come from Google can be entered in the Notes field
                  below per the existing convention. */}
              <input
                ref={addressRef}
                id="confirm-address"
                type="text"
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                onBlur={() => {
                  if (deliveryAddress.trim()) {
                    void captureInteraction({
                      surface: "order_confirm",
                      event_type: "field_edited",
                      payload: { field: "delivery_address", source: "manual" },
                      recipient_id: recipientId ?? null,
                    });
                  }
                }}
                placeholder="Start typing the office address…"
                autoComplete="off"
                /* MS158: 300 chars, matches MS145 server cap on
                   delivery_address. Long Aussie addresses with unit
                   info fit comfortably. */
                maxLength={300}
                className="w-full rounded-xl border-2 border-charcoal/15 bg-white px-3 py-2 text-base text-navy outline-none placeholder:text-charcoal/45 focus:border-coral"
              />
            </div>

            {/* NOTE-FE: first-class recipient note. Elevated from the
                old conflated "Anything else?" catch-all into the hero
                of the compose layout — this is the message the
                recipient actually reads. Still bound to the existing
                `notes` state + send path (unchanged contract); only the
                framing, label, and surface changed. The editorial
                Newsreader heading + bg-charcoal/5 card make it feel
                intentional and native to the sheet rather than a
                trailing afterthought. */}
            <div className="rounded-2xl bg-charcoal/5 p-4">
              <label htmlFor="confirm-notes" className="block">
                <span
                  className="block text-lg leading-tight text-navy"
                  style={{ fontFamily: "Newsreader, serif", fontStyle: "italic", fontWeight: 600 }}
                >
                  Your note to {noteLabelTarget}
                </span>
                <span className="mt-1 block text-xs text-charcoal/60">
                  This is the message {recipientFirstName || "they"}'ll read — write it like a card.
                </span>
              </label>
              <textarea
                id="confirm-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={() => {
                  if (notes.trim()) {
                    void captureInteraction({
                      surface: "order_confirm",
                      event_type: "field_edited",
                      payload: { field: "notes", length: notes.trim().length },
                      recipient_id: recipientId ?? null,
                    });
                  }
                }}
                placeholder={cardNotePlaceholder(occasionInput, recipientFirstName)}
                rows={3}
                /* MS158: 1000-char cap matches the MS145 server cap
                   (submit-gift-request LENGTH_CAPS.notes). NOTE-FE keeps
                   the same ceiling for the card message; buildNotesPayload
                   re-enforces 1000 across the combined card + logistics
                   string so the pair can't overflow the server limit. */
                maxLength={1000}
                className="mt-2.5 w-full resize-none rounded-xl border-2 border-charcoal/15 bg-white px-3 py-2 text-base text-navy outline-none placeholder:text-charcoal/40 focus:border-coral"
              />
              <p className="mt-1 text-right text-[11px] tabular-nums text-charcoal/45" aria-hidden="true">
                {notes.length}/1000
              </p>
            </div>

            {/* NOTE-FE: secondary, de-conflated logistics/allergy field.
                Deliberately smaller + visually quieter than the hero
                note so the recipient message stays the focus. This is
                the ops lane — gate codes, level/desk, allergy
                confirmations the dietary cross-check funnels here. It
                does NOT add a new submit-body field; buildNotesPayload
                folds it into the existing `notes` string behind a
                labelled delimiter at submit time, so the send contract
                is unchanged and this text lands in orders.notes (the
                fulfilment column) rather than the recipient's card. */}
            <div>
              <label
                htmlFor="confirm-logistics"
                className="mb-1 block text-xs font-semibold uppercase tracking-wider text-navy/70"
                style={{ letterSpacing: "0.08em" }}
              >
                For the Lembra team <span className="text-charcoal/45 normal-case font-normal" style={{ letterSpacing: 0 }}>(optional)</span>
              </label>
              <textarea
                id="confirm-logistics"
                value={logisticsNote}
                onChange={(e) => setLogisticsNote(e.target.value)}
                onBlur={() => {
                  if (logisticsNote.trim()) {
                    void captureInteraction({
                      surface: "order_confirm",
                      event_type: "field_edited",
                      payload: { field: "logistics_note", length: logisticsNote.trim().length },
                      recipient_id: recipientId ?? null,
                    });
                  }
                }}
                placeholder="Allergy details, gate code, level/desk, anything we need to deliver smoothly."
                rows={2}
                maxLength={400}
                className="w-full resize-none rounded-xl border-2 border-charcoal/15 bg-white px-3 py-2 text-sm text-navy outline-none placeholder:text-charcoal/40 focus:border-coral"
              />
            </div>

            {/* Sprint A: promo code disclosure. Collapsed by default
                so it doesn't add visual weight for the 99% of buyers
                who don't have one. Expands inline to a small input +
                Apply button when clicked. Validated state shows the
                discount line + clears via the X button. */}
            <div>
              {!promoOpen && promoState !== "valid" && (
                <button
                  type="button"
                  onClick={() => setPromoOpen(true)}
                  className="text-xs font-semibold text-navy/70 hover:text-coral focus:outline-none focus:underline"
                  style={{ fontFamily: "DM Sans, sans-serif" }}
                >
                  Have a Lembra promo code?
                </button>
              )}
              {(promoOpen || promoState === "valid") && (
                <div className="rounded-xl border border-charcoal/12 bg-white px-3 py-2.5">
                  {promoState === "valid" ? (
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p
                          className="text-[11px] font-semibold uppercase tracking-wider text-coral"
                          style={{ letterSpacing: "0.08em" }}
                        >
                          Code applied · {promoInput.trim()}
                        </p>
                        <p className="mt-0.5 text-sm text-navy">
                          −${(promoDiscountCents / 100).toFixed(2)} off · {promoMessage}
                        </p>
                      </div>
                      <button
                        type="button"
                        aria-label="Remove promo code"
                        onClick={() => {
                          setPromoState("idle");
                          setPromoMessage(null);
                          setPromoDiscountCents(0);
                          setPromoInput("");
                        }}
                        className="rounded-full p-1 text-charcoal/55 hover:text-coral focus:outline-none focus:ring-2 focus:ring-coral"
                      >
                        <X size={14} strokeWidth={2.4} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <label
                        htmlFor="promo-code"
                        className="mb-1 block text-xs font-semibold uppercase tracking-wider text-navy/70"
                        style={{ letterSpacing: "0.08em" }}
                      >
                        Promo code
                      </label>
                      <div className="flex items-stretch gap-2">
                        <input
                          id="promo-code"
                          type="text"
                          value={promoInput}
                          onChange={(e) => {
                            setPromoInput(e.target.value);
                            if (promoState !== "idle") setPromoState("idle");
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              void handleValidatePromo();
                            }
                          }}
                          placeholder="e.g. LMB-7K3PQX"
                          autoCapitalize="characters"
                          autoComplete="off"
                          maxLength={32}
                          disabled={promoState === "validating"}
                          className="flex-1 rounded-lg border-2 border-charcoal/15 bg-white px-3 py-2 text-sm text-navy outline-none placeholder:text-charcoal/40 focus:border-coral"
                          style={{ textTransform: "uppercase" }}
                        />
                        <button
                          type="button"
                          onClick={() => void handleValidatePromo()}
                          disabled={
                            promoState === "validating" || promoInput.trim().length < 3
                          }
                          className="rounded-lg bg-navy px-3 py-2 text-sm font-semibold text-white hover:bg-navy/90 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-navy"
                        >
                          {promoState === "validating" ? "…" : "Apply"}
                        </button>
                      </div>
                      {promoMessage && (
                        <p
                          role={promoState === "invalid" || promoState === "error" ? "alert" : undefined}
                          className={`mt-1.5 text-xs ${
                            promoState === "invalid" || promoState === "error"
                              ? "text-coral"
                              : "text-charcoal/65"
                          }`}
                        >
                          {promoMessage}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Reassurance line */}
            <p className="text-xs leading-relaxed text-charcoal/65">
              {promoState === "valid" && promoDiscountCents >= orderTotalCents
                ? "Your code covers this in full. We'll take you to Stripe to confirm at $0."
                : "Next, we'll take you to secure Stripe Checkout to confirm payment. Lembra coordinates fulfilment with the vendor straight after."}
            </p>

            {error && (
              <div
                role="alert"
                className="rounded-xl border border-coral/40 bg-coral/5 px-3 py-2 text-sm text-coral"
              >
                {error}
              </div>
            )}

            {/* MS355: interstate-address capture CTA. Replaces the
                standard submit when the delivery address is outside
                Melbourne metro. Logs interest into
                remote_celebration_requests so the team has one source
                of truth for "where is unmet demand?" — same table the
                chat-concierge writes to via submit_remote_request
                (MS55). On success the form transforms to a "thanks"
                confirmation so the customer has clear closure. */}
            {interstate.isInterstate && interstateRequestState !== "submitted" && (
              <div
                className="rounded-2xl border border-butter bg-butter/30 px-4 py-4 space-y-3"
                role="region"
                aria-label="Interstate delivery request"
              >
                <div>
                  <h3 className="text-base font-bold text-navy">
                    We're working on bringing local vendors to {interstate.cityLabel}
                  </h3>
                  <p className="mt-1 text-sm text-charcoal/70">
                    Our most established vendor network is in Melbourne today, so we
                    can't yet coordinate {sku.vendor_category === "voucher" || sku.is_voucher ? "this delivery" : `a ${sku.vendor_category} delivery`} to {interstate.cityLabel}. Request this and
                    we'll email you the moment we can — your interest is exactly the
                    signal that drives where we open next.
                  </p>
                </div>
                <div>
                  <label
                    htmlFor="interstate-email"
                    className="mb-1 block text-xs font-semibold uppercase tracking-wider text-navy/70"
                    style={{ letterSpacing: "0.08em" }}
                  >
                    Email for follow-up
                  </label>
                  <input
                    id="interstate-email"
                    type="email"
                    value={interstateContactEmail}
                    onChange={(e) => setInterstateContactEmail(e.target.value)}
                    placeholder={isAnonymous && guestEmail ? guestEmail : "you@company.com"}
                    autoComplete="email"
                    maxLength={200}
                    className="w-full rounded-xl border-2 border-charcoal/15 bg-white px-3 py-2 text-base text-navy outline-none focus:border-coral"
                  />
                </div>
                <div>
                  <label
                    htmlFor="interstate-notes"
                    className="mb-1 block text-xs font-semibold uppercase tracking-wider text-navy/70"
                    style={{ letterSpacing: "0.08em" }}
                  >
                    Anything else to flag? (optional)
                  </label>
                  <textarea
                    id="interstate-notes"
                    value={interstateNotes}
                    onChange={(e) => setInterstateNotes(e.target.value)}
                    placeholder="Volume you're planning, timing, what you'd love to see launch first…"
                    rows={2}
                    maxLength={600}
                    className="w-full resize-none rounded-xl border-2 border-charcoal/15 bg-white px-3 py-2 text-sm text-navy outline-none placeholder:text-charcoal/45 focus:border-coral"
                  />
                </div>
                {interstateRequestError && (
                  <p role="alert" className="text-xs text-coral">
                    {interstateRequestError}
                  </p>
                )}
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={interstateRequestState === "submitting"}
                    className="rounded-full border-2 border-navy/15 bg-white px-4 py-2 text-sm font-semibold text-navy hover:border-coral hover:text-coral disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-coral"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleInterstateRequest}
                    disabled={interstateRequestState === "submitting"}
                    className="inline-flex items-center gap-1.5 rounded-full bg-coral px-5 py-2 text-sm font-semibold text-white hover:bg-coral/85 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2"
                  >
                    {interstateRequestState === "submitting" ? (
                      <>
                        <Loader2 size={14} strokeWidth={2.5} className="animate-spin" />
                        Logging your request…
                      </>
                    ) : (
                      <>Request this for {interstate.cityLabel}</>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* MS355: post-submit confirmation. Replaces the form once
                the request lands in remote_celebration_requests. */}
            {interstate.isInterstate && interstateRequestState === "submitted" && (
              <div
                className="rounded-2xl border border-butter bg-butter/30 px-4 py-5 text-center space-y-2"
                role="status"
              >
                <h3 className="text-base font-bold text-navy">
                  Thanks — we'll be in touch
                </h3>
                <p className="text-sm text-charcoal/70">
                  We've logged your interest in {interstate.cityLabel}. We'll email
                  you the moment we can coordinate {sku.vendor_category} deliveries to
                  your area. The team reads these every week — your signal genuinely
                  shapes where we open next.
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-full border-2 border-navy/15 bg-white px-4 py-2 text-sm font-semibold text-navy hover:border-coral hover:text-coral focus:outline-none focus:ring-2 focus:ring-coral"
                >
                  Close
                </button>
              </div>
            )}

          </div>
        )}
        </div>
        {/* MS395: sticky CTA footer — always visible on short screens.
            Only shown on the main form path; recovery/redirect/interstate
            states manage their own action buttons inline. */}
        {/* MS406: hide footer while submitting — the form body is now replaced with
            CheckoutProgressState so action buttons have nothing to act on. */}
        {!recoveryRef && !redirectingTo && !submitting && !interstate.isInterstate && (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-charcoal/10 bg-white px-5 py-3.5">
            <button
              type="button"
              onClick={handleCancel}
              disabled={submitting}
              className="rounded-full border-2 border-navy/15 bg-white px-4 py-2 text-sm font-semibold text-navy hover:border-coral hover:text-coral disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-coral"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="inline-flex items-center gap-1.5 rounded-full bg-coral px-5 py-2 text-sm font-semibold text-white hover:bg-coral/85 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2"
            >
              {submitting ? (
                <>
                  <Loader2 size={14} strokeWidth={2.5} className="animate-spin" />
                  Preparing checkout…
                </>
              ) : (
                <>
                  Continue to checkout
                  <ExternalLink size={14} strokeWidth={2.5} />
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ─── Checkout progress state ────────────────────────────────────────
// MS406: unified panel shown from button click through navigation.
//
// Phase A (submitting=true, no url yet): edge function is in flight —
//   show spinner + "Preparing…", no link (there's nowhere to go yet).
// Phase B (url received): window.location.assign is about to fire —
//   show spinner + "Taking you…" + "Open checkout" fallback link.
//
// This replaces the old two-step pattern (form + button spinner for
// ~10s, then a flash of RedirectingState just before navigation).

function CheckoutProgressState({ checkoutUrl }: { checkoutUrl: string | null }) {
  const hasUrl = !!checkoutUrl;
  return (
    <div className="space-y-5 px-5 py-7 text-center" style={{ fontFamily: "DM Sans, sans-serif" }}>
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-navy text-white">
        <Loader2 size={24} strokeWidth={2.5} className="animate-spin" />
      </div>
      <div>
        <h3 className="text-xl font-semibold text-navy" style={{ fontFamily: "Newsreader, serif" }}>
          {hasUrl ? "Taking you to checkout…" : "Preparing your order…"}
        </h3>
        <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-charcoal/75">
          {hasUrl
            ? "Stripe is loading securely. If nothing happens, tap below."
            : "Hang tight. This usually takes a few seconds."}
        </p>
      </div>
      {hasUrl && (
        <a
          href={checkoutUrl!}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-coral px-5 py-2.5 text-sm font-semibold text-white hover:bg-coral/85 focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2"
        >
          Open checkout
          <ExternalLink size={14} strokeWidth={2.5} />
        </a>
      )}
    </div>
  );
}

// ─── Saved-but-no-Stripe state (recovery path) ──────────────────────
// When Stripe is unavailable but the rec + order rows succeeded —
// the customer's request is safe and Lembra ops will follow up. Better
// than failing the whole submit.

interface SavedStateProps {
  recipientName: string;
  orderReference: string;
  fulfillmentReturn?: FulfillmentReturnContext | null;
  onClose: () => void;
}

function SavedState({
  recipientName,
  orderReference,
  fulfillmentReturn = null,
  onClose,
}: SavedStateProps) {
  const firstName = recipientName.trim().split(/\s+/)[0] || "your colleague";
  return (
    <div className="space-y-5 px-5 py-7 text-center" style={{ fontFamily: "DM Sans, sans-serif" }}>
      <div>
        <h3 className="text-xl font-semibold text-navy" style={{ fontFamily: "Newsreader, serif" }}>
          Saved, we'll be in touch.
        </h3>
        <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-charcoal/75">
          Payment couldn't load just now, but {firstName}'s request is safe with us.
          Lembra will send you a payment link within a day.
        </p>
        <p className="mt-3 text-xs text-charcoal/45">Reference: {orderReference}</p>
      </div>
      {/* MS205: button text was "Back to dashboard" but onClose just
          dismisses the sheet — the user stays on whatever surface
          opened it (landing hero chat, /chat, /browse, /vendors/:slug,
          /wishlist, etc.). "Dashboard" only applies for the authed
          surfaces that wrap dashboards. "Done" is accurate everywhere
          and doesn't mislead about destination. */}
      <button
        type="button"
        onClick={() => {
          if (fulfillmentReturn) {
            fulfillmentReturn.onReturn();
          }
          onClose();
        }}
        className="w-full rounded-full bg-coral px-5 py-2.5 text-sm font-semibold text-white hover:bg-coral/85 focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2"
      >
        {fulfillmentReturn?.label ?? "Done"}
      </button>
    </div>
  );
}
